import { InputBox } from '../../../../design_system/components/Input Fields/InputBox.jsx'
import { addDaysToDate, dateFormator } from '../../../utils/dateUtils'
import { ControlSourceCard, ControlSettingRow } from './ControlsRows.jsx'
import { getFieldById } from './controlsFields.js'

// Possible SEC source states
export const SEC_SOURCE_STATES = ['disabled', 'trialPending', 'trial', 'trialExpired', 'premiumPending', 'premium']

const DataSourcesSection = ({
    currentTeam,
    values,
    originalValues,
    initialJobUpdate = {},
    canEditControls,
    isFieldVisible,
    onToggle,
    onInitialJobDaysChange,
}) => {
    const { days: initialJobDays, locked: initialJobDaysLocked } = initialJobUpdate
    const secFilingTrialDaysLeft = currentTeam?.sec_filing_trial_days_left
    const secFilingEnabledAt = currentTeam?.sec_filing_enabled_at
    const getValue = field => !!values[field.key]

    const renderNewsResults = () => {
        const newsField = getFieldById('newsfeed_enabled')
        const midConfidence = getFieldById('display_mid_confidence')
        if (!newsField || !isFieldVisible(newsField)) return null

        const isEnabled = getValue(newsField)
        return (
            <ControlSourceCard
                source="news"
                label={newsField.label}
                description={isEnabled ? newsField.description : "Hidden for everyone on this team. No news results are collected while off."}
                checked={isEnabled}
                disabled={newsField.disabled || !canEditControls}
                onToggleClick={() => onToggle(newsField.id)}
            >
                {isEnabled && midConfidence && isFieldVisible(midConfidence) && (
                    <ControlSettingRow label="Mid confidence results" description="Show results with a mid-level confidence score in the newsfeed" checked={getValue(midConfidence)} onToggleClick={() => onToggle(midConfidence.id)} disabled={!canEditControls} />
                )}
            </ControlSourceCard>
        )
    }

    const renderJobUpdates = () => {
        const jobsField = getFieldById('enable_job_updates_in_feed')
        const jobUpdates = getFieldById('display_job_updates')
        if (!jobsField || !isFieldVisible(jobsField)) return null

        const isEnabled = getValue(jobsField)
        return (
            <ControlSourceCard
                source="jobs"
                label={jobsField.label}
                description={jobsField.description}
                checked={isEnabled}
                disabled={jobsField.disabled || !canEditControls}
                onToggleClick={() => onToggle(jobsField.id)}
            >
                {isEnabled && <>
                    <ControlSettingRow label="Post initial job updates from last" description="How many days back to pull job updates when job updates are first turned on" >
                        <div className="controls-sub-setting controls-sub-setting--baseline">
                            <InputBox Size="Large" value={initialJobDays} handleOnChange={onInitialJobDaysChange} Type="Number" disabled={initialJobDaysLocked || !canEditControls} customStyle={{ width: 64 }} />
                            <span className="body-s">days</span>
                        </div>
                    </ControlSettingRow>
                    {jobUpdates && isFieldVisible(jobUpdates) && (
                        <ControlSettingRow
                            label="Job updates 2.0"
                            description="Uses natural language processing to detect and merge similar articles that fall below the confidence threshold"
                            checked={getValue(jobUpdates)}
                            onToggleClick={() => onToggle(jobUpdates.id)}
                            disabled={!canEditControls}
                        />
                    )}
                </>}
            </ControlSourceCard>
        )
    }

    const renderSecFilings = () => {
        const secField = getFieldById('sec_filings_enabled')
        const premiumField = getFieldById('sec_filings_premium')
        if (!secField || !isFieldVisible(secField)) return null
        const secSourceDescription = "10-K, 10-Q, 8-K and proxy filings from prospects' companies"

        const isEnableSecSelected = getValue(secField) // Current SEC selection state.
        const isPremiumSelected = getValue(premiumField) // Current Premium selection state.
        const isSecEnabled = !!originalValues.sec_filings_enabled // SEC enabled.
        const isPremiumEnabled = !!originalValues.sec_filings_premium // Premium enabled.
        const isExistingTrial = isSecEnabled && !isPremiumEnabled
        let secState = null
        if (!isEnableSecSelected) secState = 'disabled' // SEC is disabled.
        else if (!isPremiumSelected && (!isSecEnabled || isPremiumEnabled)) secState = 'trialPending' // Trial selected but not saved.
        else if (!isPremiumEnabled && isPremiumSelected) secState = 'premiumPending' // Premium selected but not saved.
        else if (isPremiumEnabled) secState = 'premium' // Premium is enabled.
        else if (isSecEnabled && !isPremiumEnabled) secState = 'trial' // Trial is enabled.
        let trialDaysLeft = null
        if (secState === 'trial') trialDaysLeft = secFilingTrialDaysLeft
        else if (secState === 'trialPending') trialDaysLeft = 30
        else if (secState === 'premiumPending') trialDaysLeft = isExistingTrial ? secFilingTrialDaysLeft : 30
        if (secState === 'trial' && trialDaysLeft <= 0) secState = 'trialExpired'

        const trialStart = secState === 'trialPending' ? new Date() : secFilingEnabledAt ? new Date(secFilingEnabledAt) : null
        const trialEnd = addDaysToDate(trialStart, 29)
        const trialEndDate = dateFormator(trialEnd, { type: 'input-format', locale: 'en-GB' })
        const trialStartDate = dateFormator(trialStart, { type: 'input-format', locale: 'en-GB', year: false })

        const secStatus = { label: null, variant: null, sourceDescription: null, planDescription: null, trialStatusLabel: null, trialStatusDescription: null, }
        switch (secState) {
            case 'disabled':
                secStatus.label = 'Off'
                secStatus.variant = 'off'
                secStatus.sourceDescription = "10-K, 10-Q, 8-K and proxy filings from prospects' companies. Turning this on starts a 30-day trial."
                break
            case 'trialPending':
                secStatus.label = 'Trial · starts on save'
                secStatus.variant = 'trial'
                secStatus.sourceDescription = secSourceDescription
                secStatus.planDescription = 'Trial runs for 30 days. Switch to Premium once the team has signed.'
                secStatus.trialStatusLabel = `Trial will end ${trialEndDate}`
                secStatus.trialStatusDescription = 'Starts when you save. Members see a trial banner in their SEC feed until it ends.'
                break
            case 'trial':
                secStatus.label = `Trial · ${trialDaysLeft} days left`
                secStatus.variant = 'trial'
                secStatus.sourceDescription = secSourceDescription
                secStatus.planDescription = 'Trial runs for 30 days. Switch to Premium once the team has signed.'
                secStatus.trialStatusLabel = `Trial ends ${trialEndDate}`
                secStatus.trialStatusDescription = `Started ${trialStartDate}. Members see a trial banner in their SEC feed until it ends.`
                break
            case 'trialExpired':
                secStatus.label = `Trial ended ${trialEndDate}`
                secStatus.variant = 'trial'
                secStatus.sourceDescription = 'Hidden from this team since the trial ended. Nothing new is collected.'
                secStatus.planDescription = 'Trial runs for 30 days. Switch to Premium once the team has signed.'
                secStatus.trialStatusLabel = `Trial ended ${trialEndDate}`
                secStatus.trialStatusDescription = 'Switch to Premium to bring SEC filings back for everyone, or turn the source off.'
                break
            case 'premiumPending':
                secStatus.label = 'Premium'
                secStatus.variant = 'premium'
                secStatus.sourceDescription = secSourceDescription
                secStatus.planDescription = `Premium is for teams that have signed. Switched from a trial that had ${trialDaysLeft} days left.`
                secStatus.trialStatusLabel = 'No time limit'
                secStatus.trialStatusDescription = 'Once you save, the trial banner disappears for every member and SEC filings stay on with no end date.'
                break
            case 'premium':
                secStatus.label = 'Premium'
                secStatus.variant = 'premium'
                secStatus.sourceDescription = secSourceDescription
                secStatus.planDescription = 'Premium is for teams that have signed.'
                secStatus.trialStatusLabel = 'No time limit'
                secStatus.trialStatusDescription = 'Once you save, the trial banner disappears for every member and SEC filings stay on with no end date.'
                break
            default:
                break
        }

        const setPremium = premium => {
            if (canEditControls && (secState === 'premiumPending' || secState === 'premium') !== premium) onToggle(premiumField.id)
        }
        const trialMeter = ['trialPending', 'trial', 'trialExpired'].includes(secState) && (
            <div className="controls-trial-meter" aria-label={`${30 - trialDaysLeft} of 30 days used`}>
                <span className="body-s">{30 - trialDaysLeft} of 30 days used</span>
                <span className="controls-trial-meter__track">
                    <span className={`controls-trial-meter__progress${secState === 'trialExpired' ? ' controls-trial-meter__progress--expired' : ''}`} style={{ width: `${((30 - trialDaysLeft) / 30) * 100}%` }} />
                </span>
            </div>
        )

        return (
            <ControlSourceCard
                source="sec"
                label={secField.label}
                description={secStatus.sourceDescription}
                checked={secState !== 'disabled'}
                disabled={!canEditControls}
                statusLabel={secStatus.label}
                statusVariant={secStatus.variant}
                onToggleClick={() => onToggle(secField.id)}
            >
                {secState !== 'disabled' && <>
                    <ControlSettingRow
                        label="Plan"
                        description={secStatus.planDescription}
                    >
                        <div className="controls-plan-switch" role="group" aria-label="SEC filings plan">
                            <button
                                type="button"
                                className={`body-s controls-plan-switch__option${secState !== 'premiumPending' && secState !== 'premium' ? ' controls-plan-switch__option--selected' : ''}`}
                                onClick={() => setPremium(false)}
                                disabled={!canEditControls}
                            >
                                Trial
                            </button>
                            <button
                                type="button"
                                className={`body-s controls-plan-switch__option${secState === 'premiumPending' || secState === 'premium' ? ' controls-plan-switch__option--selected' : ''}`}
                                onClick={() => setPremium(true)}
                                disabled={!canEditControls}
                            >
                                Premium
                            </button>
                        </div>
                    </ControlSettingRow>
                    <ControlSettingRow
                        label={secStatus.trialStatusLabel}
                        description={secStatus.trialStatusDescription}
                    >
                        {trialMeter}
                    </ControlSettingRow>
                </>}
            </ControlSourceCard>
        )
    }

    return (
        <div className="flex flex-col gap-s mt-m">
            {renderNewsResults()}
            {renderJobUpdates()}
            {renderSecFilings()}
        </div>
    )
}

export default DataSourcesSection
